import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { LocaleLink } from "../i18n/LocaleLink";
import {
  Hammer, Wrench, Trash2, Truck, Wind, Zap, Droplets, Trees, Settings,
  Store, Briefcase, Scissors, Dumbbell, UtensilsCrossed, Stethoscope,
  ArrowLeft, ArrowRight, Check, MapPin, Clock, FileCheck, ShieldCheck,
} from "lucide-react";
import { Container, Eyebrow, PostCard } from "../components/ui";
import {
  MotionProvider, Reveal, Workflow, type WorkflowStep, ConnectedSteps, type ConnectedStep,
  IconGridCta, type CtaGridIcon, PromoBanner, Button as DSButton, SectionHeading,
} from "../ds/core";
import { PhoneShot } from "../components/Mockups";
import { industryMapFor } from "../content/industries";
import { postMapFor, POST_DATE, coverFor } from "../content/posts";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";
import NotFound from "./NotFound";

/* ─────────────────────────────────────────────────────────────────────────
   Reusable industry template. Every section is data-driven from INDUSTRY_MAP:
   card counts follow the arrays, and sections self-disable when their data is
   empty — so a new industry is just a content entry, nothing here changes.
   ───────────────────────────────────────────────────────────────────────── */

const FRAMER = "https://framerusercontent.com/images";
const HERO_FALLBACK = `${FRAMER}/gvrZUoowU6t5rwpwtONkkVWoFSU.png`;
const U = (id: string) => `https://images.unsplash.com/${id}?w=1400&auto=format&fit=crop&q=80`;

/* One thematic photo per industry — reused across hero, steps, quote & stats. */
const INDUSTRY_PHOTO: Record<string, string> = {
  construction: U("photo-1504307651254-35680f356dfd"),
  cleaning: U("photo-1581578731548-c64695cc6952"),
  installation: U("photo-1581092580497-e0d23cbdf1dc"),
  "field-service": U("photo-1486006920555-c77dcf18193c"),
  hvac: HERO_FALLBACK,
  electricians: U("photo-1621905252507-b35492cc74b4"),
  plumbing: U("photo-1607472586893-edb57bdc0e39"),
  landscaping: U("photo-1558904541-efa843a96f01"),
  maintenance: U("photo-1581092160562-40aa08e78837"),
};
/**
 * Approved per-slot shots for the non-technical industries, uploaded to
 * Cloudinary under their CMS slug. Baked because `INDUSTRY_PHOTO` only covers
 * the nine original trades — without these, a shop or a salon fell through to
 * `HERO_FALLBACK` and showed a technician in a hard hat, which is the opposite
 * of the point. Three distinct shots per industry so hero / features / quote
 * don't repeat. The CMS still overrides each slot.
 */
const DETAIL_PHOTO: Record<string, { photo: string; feature: string; quote: string }> = {
  "beauty": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552859/industries/detail-beauty-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784631875/industries/detail-beauty-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552861/industries/detail-beauty-quote.jpg" },
  "healthcare": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552873/industries/detail-healthcare-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552874/industries/detail-healthcare-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552875/industries/detail-healthcare-quote.jpg" },
  "hospitality": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552868/industries/detail-hospitality-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552870/industries/detail-hospitality-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552871/industries/detail-hospitality-quote.jpg" },
  "offices": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552854/industries/detail-offices-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552855/industries/detail-offices-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552857/industries/detail-offices-quote.jpg" },
  "retail": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552849/industries/detail-retail-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552850/industries/detail-retail-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552851/industries/detail-retail-quote.jpg" },
  "wellness": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552864/industries/detail-wellness-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552865/industries/detail-wellness-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784552866/industries/detail-wellness-quote.jpg" },
  // The nine original trades. Their three slots used to share one photo — the
  // client's "all these pictures are still the same". Values mirror what the
  // int/en CMS already carries; every other locale had nothing and duplicated.
  "construction": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-construction-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-construction-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-construction-quote.jpg" },
  "cleaning": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-cleaning-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-cleaning-feature.png", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-cleaning-quote.jpg" },
  "installation": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-installation-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-installation-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-installation-quote.jpg" },
  "field-service": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-field-service-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-field-service-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-field-service-quote.jpg" },
  "hvac": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-hvac-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-hvac-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-hvac-quote.jpg" },
  "electricians": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-electricians-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-electricians-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-electricians-quote.jpg" },
  "plumbing": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-plumbing-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-plumbing-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-plumbing-quote.jpg" },
  "landscaping": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-landscaping-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-landscaping-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-landscaping-quote.jpg" },
  "maintenance": { photo: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-maintenance-photo.jpg", feature: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-maintenance-feature.jpg", quote: "https://res.cloudinary.com/lbx0aw1r/image/upload/industries/detail-maintenance-quote.jpg" },
};
/** Baked fallback for one slot: the approved shot, else the trade default. */
const photoFor = (slug: string, slot: "photo" | "feature" | "quote" = "photo") =>
  DETAIL_PHOTO[slug]?.[slot] || INDUSTRY_PHOTO[slug] || HERO_FALLBACK;

const GRID_W = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
} as const;
const HERO_SCRIM =
  "linear-gradient(180deg, rgba(8,11,22,0.5) 0%, rgba(8,11,22,0.35) 45%, rgba(8,11,22,0.85) 100%), linear-gradient(270deg, rgba(8,11,22,0) 35%, rgba(8,11,22,0.72) 100%)";

const ICONS: Record<string, LucideIcon> = {
  Hammer, Wrench, Trash2, Truck, Wind, Zap, Droplets, Trees, Settings,
  Store, Briefcase, Scissors, Dumbbell, UtensilsCrossed, Stethoscope,
};
const ICON_KEYS: Record<string, string> = {
  construction: "Hammer", cleaning: "Trash2", installation: "Wrench", "field-service": "Truck",
  hvac: "Wind", electricians: "Zap", plumbing: "Droplets", landscaping: "Trees", maintenance: "Settings",
  // Non-technical trades
  retail: "Store", offices: "Briefcase", beauty: "Scissors",
  wellness: "Dumbbell", hospitality: "UtensilsCrossed", healthcare: "Stethoscope",
};
// Falls back rather than returning `undefined`: an industry added to the
// content files without an ICON_KEYS entry would otherwise throw "Element type
// is invalid" and fail the whole static build, not just this page.
const iconFor = (slug: string) => ICONS[ICON_KEYS[slug]] ?? Settings;

const Y = "var(--ds-gold-ink)";
const B = "var(--ds-accent-deep)";
// `nameKey` resolves to a user-facing tile tooltip (title=) at render time.
const CTA_ICONS: { nameKey: string; Icon: LucideIcon; color: string }[] = [
  { nameKey: "ctaGrid.construction", Icon: Hammer, color: B }, { nameKey: "ctaGrid.cleaning", Icon: Trash2, color: Y },
  { nameKey: "ctaGrid.installation", Icon: Wrench, color: B }, { nameKey: "ctaGrid.fieldService", Icon: Truck, color: Y },
  { nameKey: "ctaGrid.hvac", Icon: Wind, color: B }, { nameKey: "ctaGrid.electricians", Icon: Zap, color: Y },
  { nameKey: "ctaGrid.plumbing", Icon: Droplets, color: B }, { nameKey: "ctaGrid.landscaping", Icon: Trees, color: Y },
  { nameKey: "ctaGrid.maintenance", Icon: Settings, color: B }, { nameKey: "ctaGrid.checkin", Icon: MapPin, color: Y },
  { nameKey: "ctaGrid.hours", Icon: Clock, color: B }, { nameKey: "ctaGrid.compliance", Icon: ShieldCheck, color: Y },
  { nameKey: "ctaGrid.payroll", Icon: FileCheck, color: B },
];

export default function IndustryDetail() {
  // Story 4.8: content is path-locale-driven — `industriesFor`/`postsFor` resolve
  // the locale's catalog (en=international, nl=belgium). The `market` (for the
  // final-CTA copy) reads from `useLocale()` (path-derived, correct at prerender),
  // not a path-independent client toggle.
  const { t, locale, market } = useLocale();
  const INDUSTRY_MAP = industryMapFor(locale);
  const POST_MAP = postMapFor(locale);
  // Story 4.3: content selection is path-driven (`industry/:slug`). A legacy
  // `industry?type=<slug>` URL has no path slug → client-301 to the path form,
  // keeping the active locale prefix. Direct hits with neither default to construction.
  const { lang, slug: pathSlug } = useParams();
  const [params] = useSearchParams();
  const legacy = params.get("type");
  if (!pathSlug && legacy) return <Navigate to={`/${lang}/industry/${legacy}`} replace />;
  // Past the guard, `legacy` is always null here, so it can't contribute: a path
  // slug wins, otherwise the bare path defaults to construction.
  const slug = pathSlug ?? "construction";
  // An unknown slug is a 404, not construction — the same defect the blog had:
  // the slugless-path default was also swallowing wrong slugs, so a stale or
  // foreign-market URL rendered an unrelated industry under a 404 status.
  if (pathSlug && !INDUSTRY_MAP[pathSlug]) return <NotFound />;
  const ind = INDUSTRY_MAP[slug] || INDUSTRY_MAP["construction"];
  // Three distinct photos per industry so hero, features and quote don't repeat
  // the same shot. Each its own CMS slot (falls back to the industry default).
  const photo = cmsImage(t, `industries:detail.${ind.slug}.photo`, photoFor(ind.slug, "photo"));
  const featurePhoto = cmsImage(t, `industries:detail.${ind.slug}.feature`, photoFor(ind.slug, "feature"));
  const quotePhoto = cmsImage(t, `industries:detail.${ind.slug}.quote`, photoFor(ind.slug, "quote"));

  const pains = ind.pains;
  const features = ind.features;
  const workflow = ind.workflow;
  const c = ind.compliance;
  const related = ind.relatedSlugs.map((s) => INDUSTRY_MAP[s]).filter(Boolean).slice(0, 3);
  const articles = ind.relatedArticles.map((s) => POST_MAP[s]).filter(Boolean).slice(0, 2);

  const painSteps: WorkflowStep[] = pains.map((p, i) => ({
    n: `0${i + 1}`, title: p.title, body: p.body,
  }));
  // Rotate the three slots across the steps — every step used to get the same
  // shot, which is what the client flagged ("all these pictures are the same").
  const stepPhotos = [photo, featurePhoto, quotePhoto];
  const useSteps: ConnectedStep[] = workflow.map((w, i) => ({ title: w.step, body: w.body, image: stepPhotos[i % stepPhotos.length], alt: t("industries:detail.useSteps.alt", { industry: ind.name, step: w.step }) }));
  const ctaIcons: CtaGridIcon[] = CTA_ICONS.map(({ nameKey, Icon, color }) => ({ name: t(`industries:${nameKey}`), Icon, color }));
  const lower = ind.name.toLowerCase();

  return (
    <>
      {/* 1 ── Hero: thematic photo panel, no interface elements */}
      <MotionProvider>
        <section className="relative flex justify-center px-4 font-ds md:px-[30px]">
          <div className="pointer-events-none absolute inset-x-4 top-0 bottom-4 overflow-hidden rounded-3xl bg-ds-hero">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${photo})` }} />
            <div className="absolute inset-0" style={{ background: HERO_SCRIM }} />
          </div>
          <div className="relative z-[2] flex w-full max-w-[1312px] flex-col justify-center px-4 pb-16 pt-16 md:px-10 lg:min-h-[calc(100vh-65px)]">
            <LocaleLink to="/industries" className="inline-flex w-fit items-center gap-1.5 text-ds-14 font-medium text-white/70 transition-colors hover:text-white">
              <ArrowLeft size={15} strokeWidth={2} /> {t("industries:detail.backToAll")}
            </LocaleLink>
            <div className="mt-6 max-w-[760px]">
              <Eyebrow dark>{t("industries:detail.eyebrow", { industry: ind.name })}</Eyebrow>
              <h1 className="mt-5 font-display text-ds-30 font-semibold leading-[1.08] tracking-[-1px] text-white sm:text-ds-46 lg:text-[52px]">
                {ind.headline}
              </h1>
              <p className="mt-5 max-w-[620px] text-ds-18 leading-relaxed text-white/80">
                {ind.intro}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <DSButton to={TRIAL} variant="solid-light">{t("industries:detail.cta.startTrial")}</DSButton>
                <DSButton to="/features" variant="ghost">{t("industries:detail.cta.seeAllFeatures")}</DSButton>
              </div>
            </div>
          </div>
        </section>
      </MotionProvider>

      {/* 2 ── The daily reality: blue sticky-stacking cards */}
      {painSteps.length > 0 && (
        <Workflow
          eyebrow={<Eyebrow dark>{t("industries:detail.pains.eyebrow")}</Eyebrow>}
          title={<>{t("industries:detail.pains.title", { industry: lower })} <span className="text-ds-ink">{t("industries:detail.pains.titleEmphasis")}</span></>}
          description={t("industries:detail.pains.description")}
          steps={painSteps}
          cta={{
            title: t("industries:detail.pains.cta.title"),
            body: t("industries:detail.pains.cta.body", { industry: lower }),
            action: <DSButton to={TRIAL} variant="solid-light">{t("industries:detail.cta.startTrial")}</DSButton>,
          }}
        />
      )}

      {/* 3 ── How Done-it helps: feature bento with photo + animated icons */}
      {features.length > 0 && (
        <MotionProvider>
          <section className="bg-bg font-ds leading-[1.24]">
            <Container className="py-16 md:py-20">
              <SectionHeading eyebrow={t("industries:detail.features.eyebrow")} title={t("industries:detail.features.title", { industry: lower })} />
              <div className="mt-8 rounded-3xl border border-line bg-surface p-2">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {features.map((f, i) => (
                    <Fragment key={f.title}>
                      {/* Text only. The FeatureVisual animations were keyed off
                          the feature title, so on these pages they mostly fell
                          through to a generic tile — an empty grid with a lone
                          tick, which read as broken rather than illustrative. */}
                      <Reveal className="flex">
                        <div className="flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5">
                          <h3 className="text-balance font-display text-[16px] font-semibold text-ink">{f.title}</h3>
                          <p className="mt-2 text-pretty text-ds-14 leading-relaxed text-ink-soft">{f.body}</p>
                        </div>
                      </Reveal>
                      {/* The photo sits in the 3rd cell when there are enough
                          features to fill the rows after it (the 9 trades ship
                          5). With only 3 it would leave a hole, so it moves to
                          the end and spans the row as a wide banner instead. */}
                      {((features.length >= 5 && i === 1) || (features.length < 5 && i === features.length - 1)) && (
                        <Reveal delay={0.05} className={`flex ${features.length < 5 ? "sm:col-span-2 lg:col-span-3" : ""}`}>
                          <div className="relative w-full min-h-[180px] overflow-hidden rounded-2xl">
                            <img src={featurePhoto} alt={t("industries:detail.features.photoAlt", { industry: ind.name })} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                          </div>
                        </Reveal>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
              <p className="mt-6 text-[13px] text-ink-faint">
                {t("industries:detail.features.breakdownLead")}{" "}
                <LocaleLink to="/features" className="font-medium text-primary hover:underline">{t("industries:detail.features.breakdownLink")}</LocaleLink>{t("industries:detail.features.breakdownTrail")}
              </p>
            </Container>
          </section>
        </MotionProvider>
      )}

      {/* 4 ── Two panels: dark text block + yellow mockup block. Web: side by
              side. Mobile/tablet: stacked with the mockup on top. */}
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">

            {/* Mockup panel — gold gradient, phone centred in the block.
                Mockup on top on mobile/tablet, to the right on web. */}
            <div className="relative flex items-center justify-center overflow-hidden rounded-[32px] py-12 lg:order-2 lg:w-[40%] lg:py-14" style={{ background: "linear-gradient(to bottom, var(--ds-warn), var(--ds-gold))" }}>
              <div className="w-[190px] lg:w-[215px]">
                <PhoneShot />
              </div>
            </div>

            {/* Text panel — dark */}
            <div className="relative overflow-hidden rounded-[32px] bg-ds-hero lg:order-1 lg:flex-1">
              <div className="pointer-events-none absolute inset-0" style={GRID_W} />
              <Container className="relative py-16 md:py-20 lg:min-h-[420px]">
                <div className="max-w-[560px]">
                  <SectionHeading
                    tone="dark"
                    eyebrow={c.label}
                    title={c.title}
                  />
                  <p className="mt-5 max-w-[460px] text-ds-18 leading-relaxed text-white/75">
                    {c.body}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <DSButton to={TRIAL} variant="solid-light">{t("industries:detail.cta.startTrial")}</DSButton>
                    {c.slug && POST_MAP[c.slug] && <DSButton to={`/blog/${c.slug}`} variant="ghost">{t("industries:detail.cta.readFullGuide")}</DSButton>}
                  </div>
                </div>
              </Container>
            </div>

          </div>
        </div>
      </section>

      {/* 5 ── How teams use it: sequentially lighting-up connected steps */}
      {useSteps.length > 0 && (
        <ConnectedSteps
          eyebrow={<Eyebrow>{t("industries:detail.useSteps.eyebrow", { industry: lower })}</Eyebrow>}
          title={<>{t("industries:detail.useSteps.title")} <span className="text-ds-accent">{t("industries:detail.useSteps.titleEmphasis")}</span></>}
          steps={useSteps}
        />
      )}

      {/* 6 ── Quote: one photo banner with the quote overlaid */}
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-ds-hero">
            <img src={quotePhoto} alt={t("industries:detail.quote.photoAlt", { industry: ind.name })} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            <div aria-hidden className="absolute inset-0" style={{ background: HERO_SCRIM }} />
            <div aria-hidden className="absolute inset-0" style={GRID_W} />
            <div className="relative flex items-center px-6 py-20 md:px-12 lg:min-h-[75vh] lg:py-16">
              <div className="flex max-w-[760px] items-stretch gap-5">
                <span aria-hidden className="w-1 shrink-0 rounded-full bg-ds-gold" />
                <div>
                  <div className="flex gap-0.5 text-ds-gold">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <svg key={i} width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden>
                        <path d="M9 1.5l2.09 4.24L16 6.74l-3.5 3.41.83 4.82L9 12.77l-4.33 2.27.83-4.82L2 6.74l4.91-.99L9 1.5z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="mt-5 text-ds-30 font-semibold leading-[1.2] tracking-[-0.5px] text-white lg:text-ds-34">
                    “{ind.quote.text}”
                  </blockquote>
                  <figcaption className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-ds-14 text-white/70">
                    <span className="font-semibold uppercase tracking-[0.08em]">{ind.quote.who}</span>
                    <span className="hidden h-1 w-1 rounded-full bg-white/40 sm:block" />
                    <span className="inline-flex items-center gap-1.5 font-semibold text-ds-gold">
                      <Check size={14} strokeWidth={2.6} /> {ind.quote.result}
                    </span>
                  </figcaption>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optional ── Project cross-link (construction & cleaning) → classic blue CTA */}
      {(slug === "construction" || slug === "cleaning") && (
        <PromoBanner
          eyebrow={t("industries:detail.projectCrossLink.eyebrow")}
          title={t("industries:detail.projectCrossLink.title")}
          body={t("industries:detail.projectCrossLink.body")}
          action={<DSButton to="/project-management" variant="outline">{t("industries:detail.projectCrossLink.cta")}</DSButton>}
        />
      )}

      {/* Optional ── Related reading */}
      {articles.length > 0 && (
        <section className="bg-bg-subtle font-ds">
          <Container className="py-16">
            <div className="flex items-end justify-between gap-4">
              <div>
                <SectionHeading eyebrow={t("industries:detail.relatedReading.eyebrow")} title={t("industries:detail.relatedReading.title")} />
              </div>
              <LocaleLink to="/resources" className="hidden items-center gap-1 text-[13px] font-semibold text-primary hover:underline md:flex">
                {t("industries:detail.relatedReading.allArticles")} <ArrowRight size={14} strokeWidth={2} />
              </LocaleLink>
            </div>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {articles.map((p) => (
                <PostCard
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  tag={p.tag}
                  title={p.title}
                  read={p.read}
                  date={POST_DATE[p.slug]}
                  image={coverFor(p)}
                />
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* 8 ── Also relevant: related industries as our cards */}
      {related.length > 0 && (
        <MotionProvider>
          <section className="bg-bg font-ds">
            <Container className="py-16">
              <Eyebrow>{t("industries:detail.related.eyebrow")}</Eyebrow>
              <div className="mt-6 rounded-3xl border border-line bg-surface p-2">
                <div className="grid gap-2 sm:grid-cols-3">
                  {related.map((r) => {
                    const RIcon = iconFor(r.slug);
                    return (
                      <Reveal key={r.slug} className="flex">
                        <LocaleLink to={`/industry/${r.slug}`} className="group flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5 transition-colors hover:bg-white">
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                            <RIcon size={20} strokeWidth={2} />
                          </span>
                          <h3 className="mt-4 text-balance font-display text-[16px] font-semibold text-ink">{r.name}</h3>
                          <p className="mt-1 flex-1 text-[13px] leading-relaxed text-ink-faint">{r.pains[0]?.title}</p>
                          <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                            {t("industries:detail.related.explore")} <ArrowRight size={13} strokeWidth={2} />
                          </span>
                        </LocaleLink>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </Container>
          </section>
        </MotionProvider>
      )}

      {/* 9 ── Ready when you are: icon-grid CTA with industry tiles */}
      <section className="bg-bg font-ds">
        <IconGridCta
          items={ctaIcons}
          eyebrow={<Eyebrow>{t("industries:detail.finalCta.eyebrow")}</Eyebrow>}
          title={<>{t("industries:detail.finalCta.title", { industry: lower })}</>}
          body={market !== "belgium"
            ? t("industries:detail.finalCta.body.intl")
            : t("industries:detail.finalCta.body.be")}
          actions={<DSButton to={TRIAL} variant="solid">{t("industries:detail.cta.startTrial")}</DSButton>}
        />
      </section>
    </>
  );
}

